import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { Layout, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { resolveApiErrorMessage } from "@/shared/utils/apiErrorMessage";
import { del, get } from "@/shared/utils/fetchApi";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/shared/components/ui/alert-dialog";

/**
 * PBI #78540 — Platform-wide default layouts management.
 *
 * Lists three cards (one per scope: superadmin / organization / product)
 * showing whether a default is set and basic stats (tabs/slots). The actual
 * EDITING happens by superadmin navigating to the corresponding dashboards
 * screen and clicking "Definir como Default" → "Default Global" — keeps the
 * editing UX consistent with the personal-prefs editor and avoids duplicating
 * the grid editor here.
 *
 * Only superadmin reaches this section (route guard upstream).
 */

interface PlatformDefaults {
  superadmin: PrefsSnapshot | null;
  organization: PrefsSnapshot | null;
  product: PrefsSnapshot | null;
}

interface PrefsSnapshot {
  items?: { id: string }[];
  tabs?: { id: string; items?: unknown[] }[];
  layouts?: unknown[];
}

const SCOPE_KEYS = ["superadmin", "organization", "product"] as const;

type ScopeKey = (typeof SCOPE_KEYS)[number];

function statsOf(snapshot: PrefsSnapshot | null, t: TFunction): string {
  if (!snapshot) return t("settings.dashboardLayouts.noLayout");
  if (Array.isArray(snapshot.tabs) && snapshot.tabs.length > 0) {
    const slots = snapshot.tabs.reduce(
      (sum, tab) => sum + (Array.isArray(tab.items) ? tab.items.length : 0),
      0,
    );
    return `${t("settings.dashboardLayouts.tabs", { count: snapshot.tabs.length })} · ${t("settings.dashboardLayouts.slots", { count: slots })}`;
  }
  const itemCount = Array.isArray(snapshot.items) ? snapshot.items.length : 0;
  return t("settings.dashboardLayouts.slots", { count: itemCount });
}

export function DashboardDefaultLayoutsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["platform-default-layouts"],
    queryFn: async () => {
      // v2 retorna PlatformDefaults flat (sem envelope { success, data })
      return await get<PlatformDefaults>("/dashboards/platform-defaults");
    },
    staleTime: 30 * 1000,
  });

  const clearMutation = useMutation({
    mutationFn: async (scope: ScopeKey) => {
      await del(`/dashboards/platform-defaults/${scope}`);
    },
    onSuccess: () => {
      toast.success(t("settings.dashboardLayouts.cleared"));
      queryClient.invalidateQueries({ queryKey: ["platform-default-layouts"] });
      queryClient.invalidateQueries({
        queryKey: ["dashboard-preferences-source"],
      });
    },
    onError: (err: Error) => {
      toast.error(
        t("settings.dashboardLayouts.clearFailed", {
          message: resolveApiErrorMessage(err, t),
        }),
      );
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Layout className="h-4 w-4 text-muted-foreground" />
        <h3 className="text-base font-medium">
          {t("settings.dashboardLayouts.title")}
        </h3>
      </div>
      <p className="text-sm text-muted-foreground">
        {t("settings.dashboardLayouts.intro")}{" "}
        <strong>{t("settings.dashboardLayouts.introBold")}</strong>.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {SCOPE_KEYS.map((scope) => {
          const label = t(`settings.dashboardLayouts.scopes.${scope}Label`);
          const description = t(
            `settings.dashboardLayouts.scopes.${scope}Description`,
          );
          const snapshot = data?.[scope] ?? null;
          const hasDefault = !!snapshot;
          return (
            <Card key={scope} className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium">{label}</div>
                  <div className="text-xs text-muted-foreground">
                    {description}
                  </div>
                </div>
              </div>

              <div className="text-xs">
                {isLoading ? (
                  <span className="text-muted-foreground">
                    {t("settings.dashboardLayouts.loading")}
                  </span>
                ) : (
                  <span
                    className={
                      hasDefault
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-muted-foreground"
                    }
                  >
                    {statsOf(snapshot, t)}
                  </span>
                )}
              </div>

              {hasDefault && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1" />{" "}
                      {t("settings.dashboardLayouts.clear")}
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        {t("settings.dashboardLayouts.clearTitle")}
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        {t("settings.dashboardLayouts.clearDescription", {
                          scope: label,
                        })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>
                        {t("settings.dashboardLayouts.cancel")}
                      </AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => clearMutation.mutate(scope)}
                        disabled={clearMutation.isPending}
                      >
                        {clearMutation.isPending
                          ? t("settings.dashboardLayouts.clearing")
                          : t("settings.dashboardLayouts.clear")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

export default DashboardDefaultLayoutsSection;
